/**
 * Pantalla de **Configuración** (`/admin/configuracion`). Reemplaza el placeholder
 * de `ROUTES.settings`. Equivale a `settings.html` del SSR.
 *
 * Solo-admin (permiso `all`, mismo que la API y el menú del SSR) vía
 * `RequireAdmin`. Muestra:
 *  - **Información del sistema**: usuario, rol y sede activa (de `useAuth`); el SSR
 *    mostraba lo mismo. La configuración no se persiste (el POST del SSR es no-op):
 *    no hay formulario que guardar.
 *  - **Exportaciones a Excel**: botones "Exportar hoy" y "Exportar todo" que
 *    descargan el `.xlsx` desde `/api/v1/export` (vía `useExportOrders`, que lee el
 *    blob de `lib/api` y dispara la descarga; sin `fetch` suelto). El error
 *    (404 sin órdenes, 500 sin openpyxl) se muestra legible.
 *
 * Presentacional + hooks; sin `fetch`. La exportación se acota por la sede activa
 * (`X-Sede-Id`), igual que el resto.
 */
import { Button, Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { adminErrorMessage } from "../format";
import { useExportOrders } from "../hooks/useExportOrders";
import { RequireAdmin } from "./RequireAdmin";

export function ConfigScreen() {
  const { user, activeSede } = useAuth();
  const exportOrders = useExportOrders();

  const exportError = exportOrders.isError
    ? adminErrorMessage(exportOrders.error)
    : null;

  return (
    <RequireAdmin
      title="Configuración"
      deniedMessage="No tienes permiso para acceder a la configuración."
    >
      <div className="flex flex-col gap-6">
        <h1 className="text-2xl font-semibold text-slate-900">Configuración</h1>

        <Card>
          <CardHeader>
            <CardTitle>Información del sistema</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-0.5">
                <dt className="text-sm text-slate-500">Usuario</dt>
                <dd className="text-sm font-medium text-slate-900">
                  {user?.name ?? user?.username ?? "—"}
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-sm text-slate-500">Rol</dt>
                <dd className="text-sm font-medium text-slate-900">
                  {user?.role ?? "—"}
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-sm text-slate-500">Sede activa</dt>
                <dd className="text-sm font-medium text-slate-900">
                  {activeSede?.nombre ?? "—"}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Exportaciones</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-slate-500">
              Descarga las órdenes de la sede activa en formato Excel.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="primary"
                disabled={exportOrders.isPending}
                onClick={() => exportOrders.mutate("today")}
              >
                {exportOrders.isPending && exportOrders.variables === "today"
                  ? "Exportando…"
                  : "Exportar hoy"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={exportOrders.isPending}
                onClick={() => exportOrders.mutate("all")}
              >
                {exportOrders.isPending && exportOrders.variables === "all"
                  ? "Exportando…"
                  : "Exportar todo"}
              </Button>
            </div>
            {exportError && (
              <p role="alert" className="text-sm text-red-600">
                {exportError}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </RequireAdmin>
  );
}
